import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import CardMedia from "@mui/material/CardMedia";
import { useSearchResultLayoutMobileStyles } from "./searchResultLayoutMobileStyles";
import { CompanyModel } from "../../models/company/companyModel";
import { colors } from "../../app/theme/colors";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import YouTubeIcon from "@mui/icons-material/YouTube";
import TwitterIcon from "@mui/icons-material/Twitter";
import StarIcon from "@mui/icons-material/Star";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTiktok } from "@fortawesome/free-brands-svg-icons";
import { CompanySocialMediaModel } from "../../models/company/companySocialMediaModel";
import { Env } from "../../app/redux/api";

interface Props {
  company: CompanyModel | null;
  onClick?: any;
}

export default function SearchResultLayout({ company, onClick }: Props) {
  const { t } = useTranslation();
  const { classes } = useSearchResultLayoutMobileStyles();

  const stars = Array(5)
    .fill(null)
    .map((_, i) => {
      if (i < Math.floor(company?.generalData?.numberOfStars ?? 0)) {
        return (
          <Grid item key={i}>
            <StarIcon
              fontSize="small"
              sx={{
                color: "#F0E700",
              }}
            />
          </Grid>
        );
      } else {
        return null;
      }
    });

  const handleSocialMediaClick = (event: any, url: any) => {
    event.preventDefault();
    window.open(url, "_blank");
  };

  return (
    <>
      <Grid mt={2}>
        <Card
          id="searchResultLayout-mobile_card"
          onClick={onClick}
          className={`${classes.card} ${
            company?.generalData.isPremium === true ? classes.paidCard : ""
          }`}
        >
          <Grid container display="flex" alignItems={"center"}>
            <Grid
              container
              display={"flex"}
              sx={{
                background:
                  company?.generalData.isPremium === true
                    ? colors.gold
                    : "white",
                height: "35px",
                padding: "5px",
              }}
            >
              <Grid item xs={6}>
                {company?.generalData.numberOfStars != null && (
                  <Grid
                    container
                    justifyContent={
                      company.generalData.isPremium !== false
                        ? "center"
                        : "left"
                    }
                  >
                    {stars}
                  </Grid>
                )}
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Box
                  sx={{ ml: "auto" }}
                  id="searchResultLayout-mobile_box_social-media"
                >
                  {company?.socialMedias
                    ?.reduce(
                      (
                        uniqueSocialMedias: CompanySocialMediaModel[],
                        socialMedia: CompanySocialMediaModel
                      ) => {
                        const existingSocialMedia = uniqueSocialMedias.find(
                          (sm) =>
                            sm.typeName.toLowerCase() ===
                            socialMedia.typeName.toLowerCase()
                        );
                        if (!existingSocialMedia) {
                          uniqueSocialMedias.push(socialMedia);
                        }
                        return uniqueSocialMedias;
                      },
                      []
                    )
                    .map((socialMedia, index) => {
                      let socialMediaWithColor = { ...socialMedia, color: "" };

                      switch (socialMedia.typeName.toLowerCase()) {
                        case "facebook":
                          return (
                            <FacebookOutlinedIcon
                              key={index}
                              id="searchResultLayout_icon_facebook"
                              className={classes.facebookIcon}
                              onClick={(event) =>
                                handleSocialMediaClick(
                                  event,
                                  socialMediaWithColor.url
                                )
                              }
                            />
                          );
                        case "instagram":
                          return (
                            <InstagramIcon
                              key={index}
                              id="searchResultLayout_icon_instagram"
                              className={classes.instagramIcon}
                              onClick={(event) =>
                                handleSocialMediaClick(
                                  event,
                                  socialMediaWithColor.url
                                )
                              }
                            />
                          );
                        case "twitter":
                          return (
                            <TwitterIcon
                              key={index}
                              id="searchResultLayout_icon_twitter"
                              className={classes.twitterIcon}
                              onClick={(event) =>
                                handleSocialMediaClick(
                                  event,
                                  socialMediaWithColor.url
                                )
                              }
                            />
                          );
                        case "linkedin":
                          return (
                            <LinkedInIcon
                              key={index}
                              id="searchResultLayout_icon_linkedIn"
                              className={classes.linkedInIcon}
                              onClick={(event) =>
                                handleSocialMediaClick(
                                  event,
                                  socialMediaWithColor.url
                                )
                              }
                            />
                          );
                        case "youtube":
                          return (
                            <YouTubeIcon
                              key={index}
                              id="searchResultLayout_icon_youtube"
                              className={classes.youtubeIcon}
                              onClick={(event) =>
                                handleSocialMediaClick(
                                  event,
                                  socialMediaWithColor.url
                                )
                              }
                            />
                          );
                        case "tiktok":
                          return (
                            <FontAwesomeIcon
                              icon={faTiktok}
                              key={index}
                              id="searchResultLayout_icon_tiktok"
                              className={classes.tiktokIcon}
                              onClick={(event) =>
                                handleSocialMediaClick(
                                  event,
                                  socialMediaWithColor.url
                                )
                              }
                            />
                          );
                        default:
                          return null;
                      }
                    })}
                </Box>
              </Grid>
            </Grid>
            <Grid item xs={6} mt={1}>
              <div style={{ position: "relative" }}>
                {company?.generalData.logoBase64 === null ? (
                  <Grid
                    container
                    display="flex"
                    justifyContent=""
                    alignItems="center"
                  >
                    <Avatar className={classes.avatar}>
                      {company?.generalData.name
                        ? company.generalData.name
                            .split(" ")
                            .slice(0, 2)
                            .map((word: any) => word[0])
                            .join("")
                            .toUpperCase()
                        : ""}
                    </Avatar>
                  </Grid>
                ) : (
                  <Grid
                    container
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <CardMedia
                      id="searchResultLayout_img_logo"
                      component="img"
                      className={classes.cardMedia}
                      image={Env.Prod + "/" + company?.generalData?.logoBase64}
                      style={{
                        backgroundColor: "inherit",
                        position: "relative",
                      }}
                    />
                  </Grid>
                )}
                {company?.generalData.isPremium === true && (
                  <Box className={classes.premiumMarkWrapper}>
                    <Typography variant="body2" sx={{ fontSize: "10px" }}>
                      {t("searchResultLayout.cardPremium")}
                    </Typography>
                  </Box>
                )}
              </div>
            </Grid>
            <Grid item xs={6}>
              <Grid item xs={12} textAlign={"right"}></Grid>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {company?.generalData.name}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          <CardContent>
            <Grid
              container
              display={"flex"}
              alignContent={"center"}
              justifyContent={"center"}
            >
              <Grid item xs={12} display={"flex"} mt={1}>
                <LocationCityIcon className={classes.icons} />
                <Typography variant="body1" className={classes.label}>
                  {t("searchResultLayout.cardPlace")}
                </Typography>
                <Typography variant="body1" className={classes.content}>
                  {company?.generalData.place}
                </Typography>
              </Grid>
              <Grid item xs={12} display={"flex"} mt={1}>
                <HomeOutlinedIcon className={classes.icons} />
                <Typography variant="body1" className={classes.label}>
                  {t("searchResultLayout.cardStreet")}
                </Typography>
                <Typography variant="body1" className={classes.content}>
                  {company?.generalData.street}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {company?.websites?.slice(0, 4).map((website, index) => (
                  <Grid container key={index} alignItems="center" mt={1}>
                    <OpenInNewOutlinedIcon className={classes.icons} />
                    <Typography variant="body1" className={classes.label}>
                      {t("searchResultLayout.cardWeb")}
                    </Typography>
                    <Typography variant="body1" className={classes.content}>
                      {website.url}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12}>
                {company?.phones?.slice(0, 4).map((phone, index) => (
                  <Grid container key={index} alignItems="center" mt={1}>
                    <LocalPhoneOutlinedIcon className={classes.icons} />
                    <Typography variant="body1" className={classes.label}>
                      {t("searchResultLayout.cardPhone")}
                    </Typography>
                    <Typography variant="body1" className={classes.content}>
                      {phone.phoneNumber}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Grid xs={12}>
                {company?.emails?.slice(0, 4).map((email, index) => (
                  <Grid container key={index} alignItems="center" mt={1}>
                    <EmailOutlinedIcon className={classes.icons} />
                    <Typography variant="body1" className={classes.label}>
                      {t("searchResultLayout.cardEmail")}
                    </Typography>
                    <Typography variant="body1" className={classes.content}>
                      {email.emailAddress}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Grid item md={12} xs={12} mt={2}>
                <Typography variant="body1" className={classes.description}>
                  {company?.generalData.shortDescription?.slice(0, 500)}
                </Typography>
              </Grid>
              <Grid
                item
                md={12}
                xs={12}
                lg={12}
                display="flex"
                justifyContent={"right"}
                mt={2}
              >
                {company?.categories.map((category, i) => (
              <Box
                key={i}
                className={classes.activityTagWrapper}
                sx={{ textAlign: "right", margin: "2px" }}
              >
                <Typography
                  variant="body2"
                  sx={{ textAlign: "center", fontSize: "8px" }}
                >
                  {`${category.parentCategoryName}${
                    category.childCategoryNames.length ? " - " : ""
                  }${category.childCategoryNames}`}
                </Typography>
              </Box>
            ))}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
}
